import { WebMcpCallResult, type WebMcpToolEntry, WebMcpTools } from "@minga/protocol"
import type { QueryClient } from "@tanstack/react-query"
import type { AnyRouter } from "@tanstack/react-router"
import { Schema } from "effect"
import { readError } from "@/lib/api"
import { sessionQuery } from "@/lib/queries"
import { projectsQuery } from "@/lib/workspace-api"
import { findModelContext, type ModelContext, type ModelContextTool, registerUntil } from "./model-context"
import { pageTools, placeOf } from "./page-tools"

/**
 * WebMCP (decision 0123): while the person is signed in and the browser has a model context, this tab offers a browser
 * agent Minga's tools. The external MCP's person tools come from `GET /api/webmcp/tools` and run through
 * `POST /api/webmcp/call` as the person (session cookie, same origin); the page adds `navigate`, `current_view` and
 * `send_message`. The tools are registered again whenever the person, the workspace or the project changes (the list
 * is fetched again, and a tool's required `project` defaults to the open one), and unregistered (their AbortSignal
 * aborted) on sign-out and on stop.
 */

export interface WebMcpOptions {
  readonly router: AnyRouter
  readonly queryClient: QueryClient
  /** Default: `document.modelContext ?? navigator.modelContext`. */
  readonly modelContext?: ModelContext | null
  /** Default: `GET /api/webmcp/tools`. */
  readonly fetchTools?: (signal: AbortSignal) => Promise<WebMcpTools>
  /** Default: `POST /api/webmcp/call`. */
  readonly callTool?: (
    name: string,
    args: Record<string, unknown>,
    signal: AbortSignal,
  ) => Promise<WebMcpCallResult>
  /** The tab's title (default: document.title). */
  readonly title?: () => string
  /** Test hook: how long `navigate` waits for another project's state. */
  readonly projectWaitMs?: number
}

const decodeTools = Schema.decodeUnknownSync(WebMcpTools)
const decodeResult = Schema.decodeUnknownSync(WebMcpCallResult)

export const fetchWebMcpTools = async (signal: AbortSignal): Promise<WebMcpTools> => {
  const res = await fetch("/api/webmcp/tools", {
    credentials: "same-origin",
    headers: { accept: "application/json" },
    signal,
  })
  if (!res.ok) throw await readError(res)
  return decodeTools(await res.json())
}

export const callWebMcpTool = async (
  name: string,
  args: Record<string, unknown>,
  signal: AbortSignal,
): Promise<WebMcpCallResult> => {
  const res = await fetch("/api/webmcp/call", {
    method: "POST",
    credentials: "same-origin",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({ name, arguments: args }),
    signal,
  })
  if (!res.ok) throw await readError(res)
  return decodeResult(await res.json())
}

/** The text a browser agent gets back: the tool's text blocks, as the MCP client would show them. */
export const resultText = (result: WebMcpCallResult): string => result.content.map((c) => c.text).join("\n")

/** The project a tab is on, with its name when the page has the list (for the tool descriptions). */
export interface OpenProject {
  readonly id: string
  readonly name: string | null
}

const isObject = (u: unknown): u is Record<string, unknown> =>
  typeof u === "object" && u !== null && !Array.isArray(u)

/** Does this tool require `project`? Then the open project fills it when it is left out. */
const requiresProject = (schema: Record<string, unknown>): boolean =>
  Array.isArray(schema.required) && schema.required.includes("project") && isObject(schema.properties)

/**
 * A server tool as the browser registers it: with a project open, a required `project` becomes optional (the open one
 * fills it) and says so; `execute` runs it through `call` and answers with its text.
 */
export const toModelContextTool = (
  entry: WebMcpToolEntry,
  open: OpenProject | null,
  call: (name: string, args: Record<string, unknown>, signal: AbortSignal) => Promise<WebMcpCallResult>,
): ModelContextTool => {
  const schema = entry.inputSchema as Record<string, unknown>
  const fills = open !== null && requiresProject(schema)
  const props = schema.properties as Record<string, unknown> | undefined
  const project = props && isObject(props.project) ? props.project : {}
  const label = open?.name ? `${open.name} (${open.id})` : (open?.id ?? "")
  const inputSchema = fills
    ? {
        ...schema,
        required: (schema.required as Array<unknown>).filter((k) => k !== "project"),
        properties: {
          ...props,
          project: {
            ...project,
            description: `${typeof project.description === "string" ? `${project.description} ` : ""}Default: the project open in this tab, ${label}.`,
          },
        },
      }
    : schema
  return {
    name: entry.name,
    title: entry.title,
    description: entry.description,
    inputSchema,
    annotations: entry.annotations,
    execute: async (input, { signal }) => {
      const args = { ...(isObject(input) ? input : {}) }
      if (fills && open && (typeof args.project !== "string" || args.project.trim() === ""))
        args.project = open.id
      try {
        return resultText(await call(entry.name, args, signal))
      } catch (e) {
        return JSON.stringify({ ok: false, hint: e instanceof Error ? e.message : String(e) })
      }
    },
  }
}

/** Start offering the tools; the returned function unregisters them and stops listening. No model context: a no-op. */
export const startWebMcp = (options: WebMcpOptions): (() => void) => {
  const context = options.modelContext === undefined ? findModelContext() : options.modelContext
  if (!context) return () => {}
  const { router, queryClient } = options
  const fetchTools = options.fetchTools ?? fetchWebMcpTools
  const call = options.callTool ?? callWebMcpTool
  const deps = {
    router,
    queryClient,
    ...(options.title ? { title: options.title } : {}),
    ...(options.projectWaitMs !== undefined ? { projectWaitMs: options.projectWaitMs } : {}),
  }
  let key: string | null = null
  let current: AbortController | null = null

  const register = async (signal: AbortSignal, workspaceId: string | null, projectId: string | null) => {
    const open: OpenProject | null =
      workspaceId && projectId
        ? {
            id: projectId,
            name:
              queryClient.getQueryData(projectsQuery(workspaceId).queryKey)?.find((p) => p.id === projectId)
                ?.name ?? null,
          }
        : null
    await Promise.all(pageTools(deps).map((t) => registerUntil(context, t, signal)))
    let list: WebMcpTools
    try {
      list = await fetchTools(signal)
    } catch (e) {
      if (!signal.aborted)
        console.warn(`[webmcp] tool list not loaded: ${e instanceof Error ? e.message : String(e)}`)
      return
    }
    if (signal.aborted) return
    await Promise.all(
      list.tools.map((t) => registerUntil(context, toModelContextTool(t, open, call), signal)),
    )
  }

  const sync = () => {
    const session = queryClient.getQueryData(sessionQuery.queryKey)
    const userId = session?.user.id ?? null
    const place = placeOf(router.state.location.pathname, router.state.location.search)
    const next = userId ? `${userId}|${place.workspaceId ?? ""}|${place.projectId ?? ""}` : null
    if (next === key) return
    key = next
    current?.abort()
    current = null
    if (next === null) return
    const controller = new AbortController()
    current = controller
    void register(controller.signal, place.workspaceId, place.projectId)
  }

  const offRouter = router.subscribe("onResolved", sync)
  const offQueries = queryClient.getQueryCache().subscribe((event) => {
    const k = event.query.queryKey
    if (k[0] === sessionQuery.queryKey[0] && k[1] === sessionQuery.queryKey[1]) sync()
  })
  sync()
  return () => {
    offRouter()
    offQueries()
    current?.abort()
    current = null
    key = null
  }
}
